import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FlaskConical, Sparkles, Users, MapPin, Heart, MessageSquare,
  Check, X, Loader2, ChevronRight, Play, Download, Eye,
  Zap, Target, TrendingUp, Copy, RefreshCw
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { ugcAPI } from "../lib/api";

const VARIATION_TYPES = [
  {
    id: "hooks",
    label: "Different Hooks",
    description: "Test different opening lines to grab attention",
    icon: Zap,
    color: "cyan",
    examples: ["Stop scrolling!", "POV: You discovered...", "The secret is..."]
  },
  {
    id: "ctas",
    label: "Different CTAs",
    description: "Test different calls-to-action for conversions",
    icon: Target,
    color: "green",
    examples: ["Link in bio!", "Comment YES!", "Shop now!"]
  },
  {
    id: "presenters",
    label: "Different Presenters",
    description: "Test different age groups and genders",
    icon: Users,
    color: "purple",
    examples: ["Young woman", "Adult man", "Mature woman"]
  },
  {
    id: "scenes",
    label: "Different Scenes",
    description: "Test different backgrounds and settings",
    icon: MapPin,
    color: "amber",
    examples: ["Kitchen", "Living room", "Outdoor"]
  },
  {
    id: "emotions",
    label: "Different Emotions",
    description: "Test different presenter energy levels",
    icon: Heart,
    color: "pink",
    examples: ["Excited", "Calm", "Surprised"]
  },
];

export function ABTestingModal({ 
  open, 
  onClose, 
  baseConfig,
  onBatchCreated,
  onGenerateVariation
}) {
  const [selectedTypes, setSelectedTypes] = useState(["hooks"]);
  const [countPerType, setCountPerType] = useState(2);
  const [isCreating, setIsCreating] = useState(false);
  const [previewVariations, setPreviewVariations] = useState(null);

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const totalVariations = 1 + (selectedTypes.length * countPerType); // 1 original + variations
  const estimatedTime = totalVariations * 4; // ~4 mins per video

  const handleCreateBatch = async () => {
    if (selectedTypes.length === 0) {
      toast.error("Select at least one variation type");
      return;
    }

    setIsCreating(true);
    try {
      const result = await ugcAPI.abTesting.createBatch({
        ...baseConfig,
        variation_types: selectedTypes,
        count_per_type: countPerType,
      });

      if (result.success) {
        toast.success(`Created ${result.total_variations} variations!`);
        setPreviewVariations(result);
        if (onBatchCreated) {
          onBatchCreated(result);
        }
      }
    } catch (error) {
      console.error("Failed to create batch:", error);
      toast.error(error.response?.data?.detail || "Failed to create variations");
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateAll = async () => {
    if (!previewVariations?.batch_id) return;

    setIsCreating(true);
    try {
      // Each variation goes through the normal UGC flow (queued, a few at a time)
      let queued = 0;
      for (const variation of previewVariations.variations) {
        try {
          await onGenerateVariation(variation.config);
          queued += 1;
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(detail?.message || (typeof detail === "string" ? detail : "Could not queue a variation"));
          break;
        }
      }
      if (queued > 0) {
        toast.success(`${queued} variation video${queued > 1 ? "s" : ""} queued!`, {
          description: "They'll generate a few at a time - track them in your Video Library.",
        });
        onClose();
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ab-testing-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-xl flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-cyan-400" />
            A/B Testing Variations
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Generate multiple versions of your ad to test what performs best
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {!previewVariations ? (
            <motion.div
              key="config"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6 py-4"
            >
              {/* Variation Type Selection */}
              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">What do you want to test?</Label>
                
                <div className="grid gap-3">
                  {VARIATION_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedTypes.includes(type.id);
                    
                    return (
                      <div
                        key={type.id}
                        onClick={() => toggleType(type.id)}
                        data-testid={`variation-type-${type.id}`}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? `bg-${type.color}-500/10 border-${type.color}-500`
                            : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                        }`}
                        style={{
                          backgroundColor: isSelected ? `rgb(var(--${type.color}-500) / 0.1)` : undefined,
                          borderColor: isSelected ? `rgb(var(--${type.color}-500))` : undefined
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isSelected ? `bg-${type.color}-500/20` : "bg-slate-700"
                          }`}>
                            <Icon className={`w-5 h-5 ${isSelected ? `text-${type.color}-400` : "text-slate-400"}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isSelected ? "text-white" : "text-slate-300"}`}>
                                {type.label}
                              </span>
                              {isSelected && (
                                <Check className="w-4 h-4 text-green-400" />
                              )}
                            </div>
                            <p className="text-slate-500 text-sm mt-0.5">{type.description}</p>
                            <div className="flex gap-2 mt-2">
                              {type.examples.slice(0, 3).map((ex, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-700/50 text-slate-400">
                                  {ex}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Count Per Type */}
              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">Variations per type</Label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((count) => (
                    <Button
                      key={count}
                      variant={countPerType === count ? "default" : "outline"}
                      onClick={() => setCountPerType(count)}
                      data-testid={`count-per-type-${count}`}
                      className={countPerType === count 
                        ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                        : "border-slate-600 text-slate-300"
                      }
                    >
                      {count} variation{count > 1 ? "s" : ""}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700" data-testid="variation-summary">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-sm">Total videos to generate</span>
                  <span className="text-white font-bold text-xl" data-testid="total-variations-count">{totalVariations}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-sm">Estimated time</span>
                  <span className="text-white font-medium">~{estimatedTime} minutes</span>
                </div>
                <div className="text-xs text-slate-500">
                  Includes: 1 original + {selectedTypes.length * countPerType} variations
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4 py-4"
            >
              {/* Batch Created Success */}
              <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/30 flex items-center gap-3">
                <Check className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-white font-medium">Variations Created!</p>
                  <p className="text-slate-400 text-sm">
                    {previewVariations.total_variations} variations ready to generate
                  </p>
                </div>
              </div>

              {/* Variations List */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {previewVariations.variations?.map((variation, index) => (
                  <div
                    key={variation.id}
                    className={`p-3 rounded-lg border ${
                      variation.is_original
                        ? "bg-cyan-500/10 border-cyan-500/30"
                        : "bg-slate-800/50 border-slate-700"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">
                          {index + 1}. {variation.label}
                        </span>
                        {variation.is_original && (
                          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[10px]">
                            ORIGINAL
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-slate-400 text-[10px]">
                        {variation.type}
                      </Badge>
                    </div>
                    {variation.hook_text && (
                      <p className="text-slate-400 text-xs mt-1 line-clamp-1">
                        "{variation.hook_text}"
                      </p>
                    )}
                    {variation.cta_text && (
                      <p className="text-slate-400 text-xs mt-1">
                        CTA: "{variation.cta_text}"
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Back button */}
              <Button
                variant="ghost"
                onClick={() => setPreviewVariations(null)}
                className="text-slate-400"
              >
                ← Back to options
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-slate-600 text-slate-300"
          >
            Cancel
          </Button>
          
          {!previewVariations ? (
            <Button
              onClick={handleCreateBatch}
              disabled={isCreating || selectedTypes.length === 0}
              data-testid="create-variations-btn"
              className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <FlaskConical className="w-4 h-4 mr-2" />
              )}
              Create {totalVariations} Variations
            </Button>
          ) : (
            <Button
              onClick={handleGenerateAll}
              disabled={isCreating}
              data-testid="generate-all-btn"
              className="flex-1 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-400 hover:to-cyan-400 text-white"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Generate All Videos
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ABTestingModal;
